import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { OFFICIAL_VAULTS_NS as NS } from './constants';
import { formatUsdcAmount } from '../vaults/helpers';
import type { FaucetDraft, OfferedFaucetBank } from './helpers';
import type { FaucetForm } from './useOfficialVaults';

const FIELDS: { key: keyof FaucetDraft; label: string; helper: string }[] = [
  { key: 'amount', label: `${NS}.faucet.amount`, helper: `${NS}.faucet.amountHint` },
  { key: 'userId', label: `${NS}.faucet.userId`, helper: `${NS}.faucet.userIdHint` },
];

export const FaucetDialog = ({
  form,
  bank,
}: {
  form: Pick<
    FaucetForm,
    | 'vaultId'
    | 'draft'
    | 'capped'
    | 'errors'
    | 'alert'
    | 'submitting'
    | 'balance'
    | 'balanceLoading'
    | 'closeDialog'
    | 'setField'
    | 'submit'
  >;
  bank: OfferedFaucetBank;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  const balanceText = () => {
    if (form.balanceLoading) return translate(`${NS}.faucet.balanceLoading`);
    if (!form.balance.known) return translate(`${NS}.faucet.balanceUnavailable`);
    return translate(`${NS}.faucet.bankBalance`, { amount: formatUsdcAmount(form.balance.usdc) });
  };

  return (
    <Dialog
      open={form.vaultId !== null}
      onClose={form.submitting ? undefined : form.closeDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.faucet.title`, { vaultId: form.vaultId })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">{translate(`${NS}.faucet.warning`)}</Alert>
          {form.capped && (
            <Alert severity="warning">
              {translate(`${NS}.faucet.cappedHint`, {
                max: formatUsdcAmount(form.capped.max),
                total: formatUsdcAmount(form.capped.needed),
              })}
            </Alert>
          )}
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' }}
            >
              {translate(`${NS}.faucet.bankAddress`)}: {bank.address}
            </Typography>
            <Typography variant="body2">{balanceText()}</Typography>
          </Stack>
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          {FIELDS.map(({ key, label, helper }) => (
            <TextField
              key={key}
              required
              fullWidth
              size="small"
              label={translate(label)}
              value={form.draft[key]}
              error={Boolean(form.errors[key])}
              helperText={translate(form.errors[key] ?? helper)}
              onChange={(e) => form.setField(key, e.target.value)}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.closeDialog} disabled={form.submitting}>
          {translate(`${NS}.form.cancel`)}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={form.submit}
          disabled={form.submitting}
          loading={form.submitting}
        >
          {translate(`${NS}.faucet.${form.alert?.severity === 'error' ? 'retry' : 'submit'}`)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
